import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckIcon from "@mui/icons-material/Check";

interface Props {
  open: boolean;
  onClose: () => void;
}

function openInChrome(url: string) {
  const ua = navigator.userAgent || "";
  const isAndroid = /Android/.test(ua);
  const isIOS = /iPhone|iPad/.test(ua);

  if (isAndroid) {
    // Android intent URL — opens Chrome directly
    const intent =
      `intent://${url.replace(/^https?:\/\//, "")}#Intent;` +
      `scheme=https;package=com.android.chrome;end`;
    window.location.href = intent;
  } else if (isIOS) {
    // iOS Chrome scheme
    window.location.href = url.replace(/^https?:\/\//, "googlechrome://");
  } else {
    // Fallback — open in a new tab, which may trigger the system browser
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

export default function InAppBrowserModal({ open, onClose }: Props) {
  const [copied, setCopied] = useState(false);
  const url = window.location.href;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API unavailable — show the URL as a fallback
      prompt("Copy this link and paste it in Chrome or Safari:", url);
    }
  };

  const handleOpenChrome = () => {
    openInChrome(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Open in your browser to sign in</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Google sign-in is blocked in the Twitter/X (and other in-app) browser
          for security reasons. To sign in, open this page in{" "}
          <strong>Chrome or Safari</strong>.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Button
            variant="contained"
            startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
            onClick={handleCopy}
            fullWidth
            color={copied ? "success" : "primary"}
          >
            {copied ? "Link copied!" : "Copy link"}
          </Button>

          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenChrome}
            fullWidth
          >
            Try opening in Chrome
          </Button>
        </Box>

        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: "block", mt: 2 }}
        >
          After opening in Chrome or Safari, tap "Sign in with Google" again.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
}
